import authService from './authService';
import { readData, readList, toQuery } from './apiResponse';
import type { AppUser, AppUserPatch, Profile, UsageReport, UserSession } from '../types/planning';

class ProfileService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** GET /auth/profile — quem sou eu nesta aplicação (AppUser): papel, provedor, plano e acesso. */
  async getProfile(): Promise<Profile> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/auth/profile');
    return readData<Profile>(response, 'Falha ao carregar o perfil');
  }

  /** GET /admin/users (só admin) */
  async listUsers(): Promise<AppUser[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/admin/users');
    return readList<AppUser>(response, 'Falha ao listar os usuários');
  }

  /**
   * PATCH /admin/users/:id { is_active?, cac_enabled?, plan?, access_until? } (só admin) — só os
   * campos enviados mudam; `access_until: null` = sem data de término.
   */
  async updateUser(userId: number, patch: AppUserPatch): Promise<AppUser> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/admin/users/${userId}`, patch);
    return readData<AppUser>(response, 'Falha ao atualizar o usuário');
  }

  /** @deprecated use `updateUser` */
  async patchUser(userId: number, patch: AppUserPatch): Promise<AppUser> {
    return this.updateUser(userId, patch);
  }

  /** PATCH /admin/users/:id {is_active} (só admin) */
  async setUserActive(userId: number, isActive: boolean): Promise<AppUser> {
    return this.updateUser(userId, { is_active: isActive });
  }

  /** PATCH /admin/users/:id {cac_enabled} (só admin) — liberação individual do Acervo CAC. */
  async setUserCac(userId: number, cacEnabled: boolean): Promise<AppUser> {
    return this.updateUser(userId, { cac_enabled: cacEnabled });
  }

  /** GET /admin/users/:id/sessions (Fase 8) — últimas 50 sessões, mais recente primeiro. */
  async listUserSessions(userId: number): Promise<UserSession[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/admin/users/${userId}/sessions`);
    return readList<UserSession>(response, 'Falha ao listar as sessões do usuário');
  }

  /** POST /admin/users/:id/sessions/revoke (Fase 8) — encerra todas as sessões ativas → `{ revoked }`. */
  async revokeUserSessions(userId: number): Promise<{ revoked: number }> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/admin/users/${userId}/sessions/revoke`);
    const data = await readData<{ revoked?: number } | null>(response, 'Falha ao encerrar as sessões do usuário');
    return { revoked: data?.revoked ?? 0 };
  }

  /** GET /admin/users/:id/usage?days=N (Fase 8; 1–400, padrão 30) — contadores por dia e módulo. */
  async getUserUsage(userId: number, days = 30): Promise<UsageReport> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/admin/users/${userId}/usage${toQuery({ days })}`);
    const data = await readData<Partial<UsageReport> | null>(response, 'Falha ao carregar o uso do usuário');
    return {
      days: data?.days ?? days,
      from: data?.from ?? '',
      to: data?.to ?? '',
      rows: Array.isArray(data?.rows) ? data.rows : [],
      totals: data?.totals ?? {},
      total: data?.total ?? 0,
    };
  }
}

export const profileService = new ProfileService();
export default profileService;
